import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-scroll';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(255, 107, 107, 0.05) 100%);
  padding: 6rem 0 4rem;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    text-align: center;
  }
`;

const TextContent = styled.div`
  flex: 1;
  max-width: 600px;
`;

const Subtitle = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 0.875rem;
  font-family: ${({ theme }) => theme.fonts.logo};
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const Highlight = styled.span`
  color: ${({ theme }) => theme.colors.black};
  position: relative;
  font-style: italic;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 0;
    width: 100%;
    height: 10px;
    background-color: rgba(255, 107, 107, 0.3);
    z-index: -1;
    border-radius: 2px;
  }
`;

const Description = styled.p`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 2.5rem;
  line-height: 1.7;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const PrimaryButton = styled(Link)`
  background-color: ${({ theme }) => theme.colors.black};
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.875rem;
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  border: 2px solid transparent;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: ${({ theme }) => theme.colors.black};
  padding: 1rem 2.5rem;
  border-radius: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.875rem;
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  border: 2px solid ${({ theme }) => theme.colors.black};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.black};
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-top: 3rem;
  }
`;

const HeroImage = styled.div`
  width: 100%;
  max-width: 600px;
  height: 500px;
  background-color: ${({ theme }) => theme.colors.black};
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=1469&q=80') center/cover no-repeat;
    filter: grayscale(100%) contrast(1.2);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, rgba(0,0,0,0.3) 0%, rgba(255,107,107,0.2) 100%);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 350px;
  }
`;

const Shape = styled.div`
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.black} 0%, ${({ theme }) => theme.colors.accent} 100%);
  opacity: 0.03;
  z-index: 0;
  
  &:nth-child(1) {
    width: 500px;
    height: 500px;
    top: -250px;
    right: -250px;
  }
  
  &:nth-child(2) {
    width: 300px;
    height: 300px;
    bottom: -150px;
    left: -150px;
  }
`;

const Hero = () => {
  return (
    <HeroSection id="home">
      <Shape />
      <Shape />
      <div className="container">
        <HeroContent>
          <TextContent>
            <Subtitle>Witamy w Adara Dance Studio</Subtitle>
            <Title>
              Odkryj <Highlight>magię</Highlight> tańca z nami
            </Title>
            <Description>
              Jesteśmy pasjonatami tańca z Zielonej Góry, którzy dzielą się miłością do ruchu i muzyki. Oferujemy profesjonalne zajęcia taneczne dla wszystkich poziomów zaawansowania.
            </Description>
            <ButtonGroup>
              <PrimaryButton 
                to="contact" 
                spy={true} 
                smooth={true} 
                duration={500}
                offset={-80}
              >
                Zapisz się na zajęcia
              </PrimaryButton>
              <SecondaryButton 
                to="portfolio" 
                spy={true} 
                smooth={true} 
                duration={500}
                offset={-80}
              >
                Zobacz nasze style
              </SecondaryButton>
            </ButtonGroup>
          </TextContent>
          <ImageContainer>
            <HeroImage />
          </ImageContainer>
        </HeroContent>
      </div>
    </HeroSection>
  );
};

export default Hero;
